import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { dict } from "../i18n";
import { sendInquiry } from "../lib/api";

export default function InquiryForm({ lang, variant = "full" }) {
  const t = dict[lang].form;
  const [form, setForm] = useState({
    inquiry_type: "appointment",
    patient_name: "",
    patient_email: "",
    patient_phone: "",
    patient_id_code: "",
    preferred_date: "",
    preferred_location: "Tallinn",
    message: "",
    website: "", // honeypot — keep empty
  });
  const [status, setStatus] = useState({ state: "idle" }); // idle | sending | sent | error

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const showIdCode = form.inquiry_type === "appointment" || form.inquiry_type === "prescription";
  const showPhone = form.inquiry_type !== "prescription";
  const showDate = form.inquiry_type !== "prescription";

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "sending" });
    try {
      await sendInquiry({ ...form, language: lang });
      setStatus({ state: "sent" });
    } catch (err) {
      const code = err?.response?.data?.detail;
      const localized = t.errors?.[code];
      setStatus({ state: "error", msg: localized || null });
    }
  };

  const compact = variant === "compact";

  return (
    <div className={compact ? "" : "card-clinic"} data-testid="inquiry-form-root">
      {!compact && (
        <>
          <h3 className="text-xl font-semibold text-surface-ink mb-1">{t.title}</h3>
          <p className="text-[14px] text-surface-inkSoft mb-6">{t.subtitle}</p>
        </>
      )}

      {status.state === "sent" ? (
        <div
          className="flex items-start gap-3 rounded-xl bg-teal-200/50 border border-teal-300/50 p-4"
          data-testid="inquiry-form-success"
        >
          <CheckCircle2 className="h-5 w-5 text-teal-500 mt-0.5 shrink-0" />
          <div className="text-[14px] text-teal-700">{t.success}</div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
          {/* Honeypot — hidden from humans via aria + offscreen; bots fill it */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
            <label>Website (do not fill)</label>
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={set("website")}
              name="website"
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-surface-inkSoft mb-1.5">{t.type}</label>
            <select
              value={form.inquiry_type}
              onChange={set("inquiry_type")}
              data-testid="inquiry-type-select"
              className="w-full px-4 py-3 rounded-lg bg-white border border-surface-outline/60 text-[14px] focus:outline-none focus:border-sapphire-500 focus:ring-2 focus:ring-teal-200/40"
            >
              {Object.entries(t.types).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div className={compact ? "" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
            <Input label={t.name} value={form.patient_name} onChange={set("patient_name")} required testid="inquiry-name" />
            <Input label={t.email} type="email" value={form.patient_email} onChange={set("patient_email")} required testid="inquiry-email" />
          </div>

          {showIdCode && (
            <Input
              label={t.idCode}
              hint={t.idCodeHint}
              value={form.patient_id_code}
              onChange={set("patient_id_code")}
              required
              placeholder="39001011234"
              inputMode="numeric"
              testid="inquiry-id-code"
            />
          )}

          {(showPhone || showDate) && (
            <div className={compact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
              {showPhone && <Input label={t.phone} value={form.patient_phone} onChange={set("patient_phone")} required={form.inquiry_type === "appointment"} testid="inquiry-phone" />}
              {showDate && <Input label={t.date} value={form.preferred_date} onChange={set("preferred_date")} placeholder="2026-03-15 14:00" testid="inquiry-date" />}
            </div>
          )}

          <div>
            <label className="block text-[13px] font-medium text-surface-inkSoft mb-1.5">{t.message}</label>
            <textarea
              value={form.message}
              onChange={set("message")}
              required
              rows={compact ? 3 : 4}
              placeholder={t.placeholderByType?.[form.inquiry_type] || t.placeholderMsg}
              data-testid="inquiry-message"
              className="w-full px-4 py-3 rounded-lg bg-white border border-surface-outline/60 text-[14px] focus:outline-none focus:border-sapphire-500 focus:ring-2 focus:ring-teal-200/40"
            />
          </div>

          {status.state === "error" && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-[13px] text-red-700" data-testid="inquiry-form-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {status.msg || t.error}
            </div>
          )}

          <button
            type="submit"
            disabled={status.state === "sending"}
            data-testid="inquiry-submit-btn"
            className="btn-primary w-full"
          >
            {status.state === "sending" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> {t.sending}
              </>
            ) : (
              t.submit
            )}
          </button>
        </form>
      )}
    </div>
  );
}

function Input({ label, hint, testid, ...rest }) {
  return (
    <div>
      <label className="block text-[13px] font-medium text-surface-inkSoft mb-1.5">{label}</label>
      <input
        {...rest}
        data-testid={testid}
        className="w-full px-4 py-3 rounded-lg bg-white border border-surface-outline/60 text-[14px] focus:outline-none focus:border-sapphire-500 focus:ring-2 focus:ring-teal-200/40"
      />
      {hint && <div className="text-[12px] text-surface-inkSoft mt-1">{hint}</div>}
    </div>
  );
}
